<template>
  <!-- Renderlos: steuert nur den Gratis-Artikel im Warenkorb, keine sichtbare UI. -->
  <span class="gift-manager" style="display: none" aria-hidden="true"></span>
</template>

<script>
export default {
  name: 'gift-manager',

  props: {
    // Varianten-ID des Gratis-Artikels (Pflicht).
    variationId: {
      type: [Number, String],
      required: true,
    },
    // Auslöser: 'value' = nach Warenwert, 'coupon' = nach eingelöstem Gutscheincode.
    trigger: {
      type: String,
      default: 'value',
    },
    // Warenwert-Schwelle, ab der das Geschenk automatisch in den Warenkorb kommt
    // (nur bei trigger = 'value').
    minBasketValue: {
      type: [Number, String],
      default: 0,
    },
    // Gutscheincode, der das Geschenk auslöst (nur bei trigger = 'coupon').
    couponCode: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      // Verhindert parallele Add/Remove-Aktionen (sonst Endlosschleife durch die
      // von jeder Warenkorb-Änderung ausgelösten Watcher).
      isSyncing: false,
      // Verhindert Endlos-Retry, falls das Hinzufügen scheitert (z. B. Aktion
      // nicht konfiguriert). Wird bei jeder echten Warenwert-Änderung gelöst.
      addBlocked: false,
      // DEBUG: auf true setzen, um alle Konsolen-Ausgaben einzuschalten.
      debug: false,
    }
  },

  computed: {
    minValue() {
      return Number(this.minBasketValue) || 0
    },
    targetCoupon() {
      return String(this.couponCode || '').toUpperCase()
    },
    basketItems() {
      return (this.$store.state.basket && this.$store.state.basket.items) || []
    },
    basketReady() {
      return !!(this.$store.state.basket && this.$store.state.basket.data)
    },
    // Aktuell im Warenkorb eingelöster Gutscheincode (normalisiert).
    appliedCoupon() {
      const data = this.$store.state.basket && this.$store.state.basket.data
      return data && data.couponCode ? String(data.couponCode).toUpperCase() : ''
    },
    // Ist die konfigurierte Bedingung gerade erfüllt? Je nach Auslöser Warenwert
    // ODER eingelöster Gutschein.
    conditionMet() {
      if (this.trigger === 'coupon') {
        return !!this.targetCoupon && this.appliedCoupon === this.targetCoupon
      }
      return this.minValue > 0 && this.basketValueWithoutGift >= this.minValue
    },
    // Warenkorb-Position des Gratis-Artikels (für gezieltes Entfernen).
    giftItem() {
      return this.basketItems.find(
          (basketItem) => Number(basketItem.variationId) === Number(this.variationId),
      )
    },
    // Warenwert OHNE das Geschenk - sonst hält es sich selbst über der Schwelle
    // bzw. blockiert das Entfernen. Hängt bewusst an itemSum UND an basketItems,
    // damit der Watcher auch bei reinen Mengenänderungen sicher auslöst.
    basketValueWithoutGift() {
      const data = this.$store.state.basket && this.$store.state.basket.data
      let total = data && data.itemSum != null ? Number(data.itemSum) : NaN
      if (isNaN(total)) {
        total = this.basketItems.reduce(
            (sum, bi) => sum + (Number(bi.price) || 0) * bi.quantity, 0,
        )
      }
      const gift = this.giftItem
      const giftValue = gift ? (Number(gift.price) || 0) * gift.quantity : 0
      return total - giftValue
    },
  },

  watch: {
    // Jede Änderung des Warenwerts (Artikel/Menge) neu bewerten. immediate deckt
    // den Fall ab, dass der Warenkorb schon beim Mount über der Schwelle liegt.
    basketValueWithoutGift: {
      immediate: true,
      handler(newVal, oldVal) {
        this.log('watch basketValueWithoutGift:', oldVal, '->', newVal, '| Schwelle:', this.minValue)
        this.addBlocked = false
        this.evaluate()
      },
    },
    // Änderung des eingelösten Gutscheins neu bewerten (nur relevant bei trigger 'coupon').
    appliedCoupon(newVal, oldVal) {
      this.log('watch appliedCoupon:', oldVal, '->', newVal, '| Ziel:', this.targetCoupon)
      this.addBlocked = false
      this.evaluate()
    },
    // Falls der Warenkorb erst nach dem Mount geladen wird.
    basketReady(ready) {
      this.log('watch basketReady ->', ready)
      if (ready) {
        this.evaluate()
      }
    },
  },

  mounted() {
    this.log('mounted | trigger:', this.trigger, '| variationId:', this.variationId,
        '| minBasketValue:', this.minBasketValue, '(-> minValue:', this.minValue + ')',
        '| couponCode:', this.targetCoupon,
        '| basketReady:', this.basketReady, '| items:', this.basketItems.length)
  },

  methods: {
    log(...args) {
      if (this.debug) {
        // eslint-disable-next-line no-console
        console.log('[GiftManager]', ...args)
      }
    },

    evaluate() {
      const met = this.conditionMet
      const inBasket = !!this.giftItem
      this.log('evaluate | trigger:', this.trigger, '| Bedingung erfüllt:', met,
          '| Warenwert(ohne Geschenk):', this.basketValueWithoutGift, '| Schwelle:', this.minValue,
          '| Coupon:', this.appliedCoupon, '/', this.targetCoupon,
          '| imKorb:', inBasket, '| ready:', this.basketReady,
          '| syncing:', this.isSyncing, '| addBlocked:', this.addBlocked)

      if (!this.basketReady || this.isSyncing) {
        this.log('-> Abbruch (Warenkorb nicht bereit ODER läuft gerade)')
        return
      }
      if (this.trigger === 'value' && this.minValue <= 0) {
        this.log('-> Abbruch (Auslöser Warenwert, aber keine Schwelle gesetzt)')
        return
      }
      if (this.trigger === 'coupon' && !this.targetCoupon) {
        this.log('-> Abbruch (Auslöser Gutschein, aber kein Code gesetzt)')
        return
      }

      if (met && !inBasket && !this.addBlocked) {
        this.log('-> AUSLÖSER: Bedingung erfüllt, Geschenk wird hinzugefügt')
        this.addGift()
      } else if (!met && inBasket) {
        this.log('-> Bedingung nicht mehr erfüllt, Geschenk wird entfernt')
        this.removeGift()
      } else {
        this.log('-> keine Aktion nötig')
      }
    },

    addGift() {
      this.log('addGift: dispatch addBasketItem variationId', Number(this.variationId))
      this.isSyncing = true
      this.$store.dispatch('addBasketItem', {
        variationId: Number(this.variationId),
        quantity: 1,
        basketItemOrderParams: [],
        totalOrderParamsMarkup: 0,
      }).then(
          (res) => {
            this.isSyncing = false
            this.log('addGift OK | Antwort:', res, '| jetzt imKorb:', !!this.giftItem)
            // Ist der Artikel trotz Erfolg nicht drin (z. B. nicht verkäuflich),
            // nicht sofort erneut versuchen.
            if (!this.giftItem) {
              this.log('addGift: Artikel trotz Erfolg NICHT im Korb -> addBlocked gesetzt')
              this.addBlocked = true
            }
          },
          (err) => {
            this.isSyncing = false
            this.addBlocked = true
            // Grund moeglichst lesbar herausziehen (Ceres/Plenty-Fehlerformat variiert).
            var detail = err
            try {
              detail = JSON.stringify(err, function (k, v) {
                return v instanceof Error ? { name: v.name, message: v.message } : v
              })
            } catch (e) { /* zirkulaer -> Rohobjekt loggen */ }
            this.log('addGift FEHLER | dispatch abgelehnt:', err)
            this.log('addGift FEHLER | Detail:', detail)
            this.log('addGift FEHLER | data:', err && err.data, '| events:', err && err.events)
          },
      )
    },

    removeGift() {
      const gift = this.giftItem
      if (!gift) {
        return
      }
      this.log('removeGift: dispatch removeBasketItem id', gift.id)
      this.isSyncing = true
      this.$store.dispatch('removeBasketItem', gift.id).then(
          () => { this.isSyncing = false; this.log('removeGift OK') },
          (err) => { this.isSyncing = false; this.log('removeGift FEHLER:', err) },
      )
    },
  },
}
</script>
